/**
 * bavona-gift.js
 * -----------------------------------------------------------------------
 * Client-side brain for the "Complimentary Perfume Butter" gift selector.
 * Every order includes ONE free 5g Perfume Butter; the shopper picks the
 * scent from the CART (cart page + cart drawer). This file is standalone
 * vanilla JS (no jQuery, no framework) and is `defer`-loaded AFTER
 * theme.js (see layout/theme.liquid) so `window.theme.miniCart` and
 * `window.__bvCartRefresh` already exist by the time it runs.
 *
 * DATA CONTRACT — #bavona-gift-data (see snippets/bavona-gift-data.liquid):
 *   { enabled, productTitle, defaultVariantId, variants:[{id,title,family,
 *     available,image,isDefault}] }
 *   When disabled the island is exactly {"enabled":false} and this whole
 *   module no-ops (see the very first guard below).
 *
 * DOM CONTRACT — cart page (see snippets/bavona-gift-selector.liquid):
 *   Server-rendered once inside sections/bavona-cart.liquid. Root
 *   `.bv-gift[data-bavona-gift]` always ships `hidden`; we remove that
 *   attribute once we know the feature is enabled. All copy (heading,
 *   subheading, summary/legend/footnote labels, the "Included — {scent}"
 *   template) is ALREADY server-rendered there — we never rebuild it.
 *
 * DOM CONTRACT — cart drawer (mini-cart, `.js-mini-cart-content`):
 *   No selector markup is server-rendered here (only the JSON island is
 *   global — see layout/theme.liquid). This module builds the drawer
 *   variant entirely from island data.
 *
 *   The drawer deliberately does NOT mirror the cart page's markup. It
 *   renders a lede line plus an always-visible horizontal strip of scent
 *   chips (`.bv-gift__strip` / `.bv-gift__chip`) with NO <details> panel:
 *   customer feedback (Aug 2026) was that nobody realised the scent could
 *   be changed at all. The old trigger was a 10px uppercase <summary> with
 *   its disclosure marker hidden — visually identical to this brand's
 *   decorative eyebrow labels, so it read as a caption rather than a
 *   control. Since nearly all traffic is mobile, that made the drawer the
 *   only surface most shoppers ever saw, and the choice invisible on it.
 *   Only the RADIO contract is shared with the cart page
 *   (`input[name="bavona-gift-scent"]` + `data-variant-id`), which is what
 *   lets one delegated `change` listener drive both surfaces.
 *
 *   The cart PAGE keeps its <details> panel and family-grouped grid — it
 *   has room for a heading and subheading, so discovery was never the
 *   problem there.
 *
 * COPY SOURCE: the data-island carries a `strings` object emitted straight
 *   from the `sections.cart.gift.*` locale keys (see
 *   snippets/bavona-gift-data.liquid), so drawer copy is authored in
 *   locales/*.json like everything else. The DRAWER_STRINGS_* tables in
 *   section 1 are now only a last-resort fallback for an island rendered
 *   before `strings` existed. This closes the contract gap previously
 *   flagged here.
 *
 * LOOP SAFETY (see reconcile()/mutate() below for the implementation):
 *   A single module-level `busy` flag is set for the full lifetime of any
 *   cart mutation this module performs (add/change → surface refresh).
 *   Every reconcile() entry point bails immediately while busy. Combined
 *   with reconcile() being fully idempotent (it only ever acts when the
 *   cart is DEMONSTRABLY wrong, and does nothing once state is correct),
 *   this makes an add → re-render → reconcile → add loop structurally
 *   impossible: (1) while our own mutation is in flight, every reactive
 *   re-render this module triggers (generateCart/updateElements wrap,
 *   our own `cart:updated` self-dispatch) calls reconcile(), which bails
 *   on `busy`; (2) once busy clears, the cart is already in the correct
 *   state, so any further reconcile() call — even one that races the
 *   `busy` flag clearing — evaluates real conditions and finds nothing
 *   left to do (the "do nothing" terminal branch). See the inline proof
 *   comment above reconcile() for the single-threaded-JS argument that
 *   two concurrent reconcile() calls can never both decide to mutate.
 * -----------------------------------------------------------------------
 */
(function () {

  /* =======================================================================
   * 0. Parse the data-island. Silent no-op if the feature is off/missing —
   *    nothing below this block ever runs in that case.
   * ===================================================================== */
  var dataEl = document.getElementById('bavona-gift-data');
  var GIFT = null;
  if (dataEl) {
    try {
      GIFT = JSON.parse(dataEl.textContent || dataEl.innerText || '{}');
    } catch (e) {
      GIFT = null;
    }
  }
  if (!GIFT || !GIFT.enabled) return; // feature disabled/blank — clean no-op

  var variants = Array.isArray(GIFT.variants) ? GIFT.variants : [];

  // giftVariantIds: id (string) -> variant record, for O(1) lookups and to
  // double as the "is this variant id a gift variant" Set the spec asks for.
  var giftVariantIds = {};
  variants.forEach(function (v) {
    giftVariantIds[String(v.id)] = v;
  });

  function isGiftVariantId(id) {
    return Object.prototype.hasOwnProperty.call(giftVariantIds, String(id));
  }

  function isGiftItem(item) {
    return !!item && (
      isGiftVariantId(item.variant_id) ||
      (item.properties && item.properties._gift === 'true')
    );
  }

  /* Ritual Pouch offer (owner, 2026-10-02). One pouch "with our compliments"
   * once QUALIFYING products reach POUCH.thresholdCents. The price is set to
   * 0 server-side by the automatic Buy X Get Y discount (buys: collection
   * "Gift-Qualifying Products", which excludes the gift and the pouch); this
   * module only keeps the cart in step: it auto-adds one pouch line tagged
   * `_pouch_gift:'true'` and removes that line again below the threshold.
   * A pouch the shopper added herself (no property) is never touched.
   * The pouch is ALSO excluded from the perfume-butter gift qualification:
   * a pouch-only cart does not earn the complimentary butter. */
  // Identity is known even when the auto-add is switched off, so a
  // pouch-only cart never earns the butter regardless of the toggle.
  var POUCH = (GIFT.pouch && GIFT.pouch.enabled) ? GIFT.pouch : null;
  var pouchVariantIds = {};
  if (GIFT.pouch && Array.isArray(GIFT.pouch.variantIds)) {
    GIFT.pouch.variantIds.forEach(function (id) { pouchVariantIds[String(id)] = true; });
  }
  var pouchAddFailed = false; // stop retrying for this page view after a failed add

  function isPouchItem(item) {
    return !!item && Object.prototype.hasOwnProperty.call(pouchVariantIds, String(item.variant_id));
  }

  function isAutoPouchItem(item) {
    return isPouchItem(item) && !!item.properties && item.properties._pouch_gift === 'true';
  }

  // Owner decision: no `is_default` flag will be set — lead with the first
  // AVAILABLE variant. defaultVariantId is only honored if it is itself
  // available; a sold-out "default" is never auto-selected.
  function resolveDefaultVariantId() {
    if (GIFT.defaultVariantId != null && isGiftVariantId(GIFT.defaultVariantId)) {
      var flagged = giftVariantIds[String(GIFT.defaultVariantId)];
      if (flagged.available) return flagged.id;
    }
    for (var i = 0; i < variants.length; i++) {
      if (variants[i].available) return variants[i].id;
    }
    return null; // nothing purchasable — nothing this module can do
  }

  /* =======================================================================
   * 1. Drawer-only chrome copy. See the "KNOWN CONTRACT GAP" note above —
   *    these mirror locales/*.json `sections.cart.gift.*` verbatim and are
   *    used ONLY to build the compact drawer variant on pages where no
   *    localized DOM source exists. The cart-page selector never touches
   *    this table (its copy is already server-rendered).
   * ===================================================================== */
  function isRTL() {
    return document.documentElement.getAttribute('dir') === 'rtl';
  }

  var DRAWER_STRINGS_EN = {
    drawerLede: 'A gift from the House — choose your scent',
    change: 'Change scent',
    familyWarm: 'Warm & Gourmand',
    familyFloral: 'Floral & Fresh',
    ariaChoose: 'Choose your complimentary Perfume Butter scent',
    complimentary: 'Complimentary',
    selectedPrefix: 'Included — {scent}',
    soldOut: 'Sold out'
  };
  var DRAWER_STRINGS_AR = {
    drawerLede: 'هديّة من الدار — اختاري عطركِ',
    change: 'تغيير العطر',
    familyWarm: 'دافئة وشهيّة',
    familyFloral: 'زهريّة ومنعشة',
    ariaChoose: 'اختاري عطر زبدة العطر المُهداة إليكِ من الدار',
    complimentary: 'هديّة من الدار',
    selectedPrefix: 'ضِمن هديّتكِ — {scent}',
    soldOut: 'نفدت الكمية'
  };

  function getDrawerStrings() {
    var base = isRTL() ? DRAWER_STRINGS_AR : DRAWER_STRINGS_EN;

    // PREFERRED SOURCE: `strings` on the data-island, emitted by
    // snippets/bavona-gift-data.liquid straight from the `sections.cart.gift.*`
    // locale keys. This closes the contract gap flagged in the header comment —
    // copy now lives in locales/*.json only, and the tables above are a
    // last-resort mirror for an island rendered before `strings` existed.
    var out = {};
    Object.keys(base).forEach(function (k) { out[k] = base[k]; });
    if (GIFT.strings && typeof GIFT.strings === 'object') {
      Object.keys(out).forEach(function (k) {
        var v = GIFT.strings[k];
        if (typeof v === 'string' && v !== '') out[k] = v;
      });
    }

    // The cart-page selector, when present, is the most authoritative source
    // for the "Included — {scent}" template (it is rendered by the section
    // itself, so it survives any island staleness).
    var liveRoot = document.querySelector('[data-bavona-gift][data-gift-selected-template]');
    if (liveRoot) {
      out.selectedPrefix = liveRoot.getAttribute('data-gift-selected-template') || out.selectedPrefix;
    }
    return out;
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* =======================================================================
   * 2. Cart AJAX API helpers. Plain fetch, mirroring the exact idiom
   *    already used by sections/bavona-cart.liquid's own inline script
   *    (no jQuery/Shopify.* dependency for these calls).
   * ===================================================================== */
  function getCartData() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  // cartSectionParams(): the cart-page section id(s) currently on the page,
  // so every mutation can ask /cart/{add,change}.js to return the freshly
  // rendered cart section HTML in the SAME response (atomic). Empty on
  // non-cart pages (drawer-only) — then the mutation returns plain cart JSON
  // and applyCartSections() is a no-op, exactly as before.
  function cartSectionParams() {
    var ids = Object.keys(window.__bvCartRefresh || {});
    if (!ids.length) return null;
    return { sections: ids.join(','), sections_url: window.location.pathname };
  }

  // applyCartSections(data): swap in any cart-page section HTML returned
  // atomically alongside a mutation (via window.__bvCartApply, exposed by
  // sections/bavona-cart.liquid). This replaces the old, race-prone separate
  // GET ?sections= refresh — the HTML here is guaranteed to reflect the cart
  // AFTER this mutation, so removing the gift never leaves a stale gift row.
  function applyCartSections(data) {
    if (!data || !data.sections) return;
    var applyMap = window.__bvCartApply || {};
    Object.keys(data.sections).forEach(function (id) {
      if (data.sections[id] != null && typeof applyMap[id] === 'function') {
        applyMap[id](data.sections[id]);
      }
    });
  }

  function cartAdd(variantId, properties) {
    var body = { id: variantId, quantity: 1, properties: properties || { _gift: 'true' } };
    var sp = cartSectionParams();
    if (sp) { body.sections = sp.sections; body.sections_url = sp.sections_url; }
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (d) { throw d; });
      return r.json();
    });
  }

  function cartChange(lineKey, quantity) {
    var body = { id: lineKey, quantity: quantity };
    var sp = cartSectionParams();
    if (sp) { body.sections = sp.sections; body.sections_url = sp.sections_url; }
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (d) { throw d; });
      return r.json();
    });
  }

  /* =======================================================================
   * 3. Gift-line identity — getGiftLine(cart) = first cart line whose
   *    variant is a known gift variant OR carries `_gift:'true'` (kept as
   *    a fallback identity in case the gift product's variant ids ever
   *    change on the merchant side).
   * ===================================================================== */
  function getGiftLine(cart) {
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      if (isGiftItem(items[i])) return items[i];
    }
    return null;
  }

  /* =======================================================================
   * 4. Loop safety primitive + the single mutation gateway. Every cart
   *    mutation this module performs MUST go through mutate() so `busy`
   *    is always set for the full add/change → surface-refresh round trip.
   * ===================================================================== */
  var busy = false;

  function mutate(actionFn, onFail) {
    busy = true;
    var ok = false;
    return actionFn()
      .then(function (data) {
        ok = true;
        // Apply the cart-page section HTML returned ATOMICALLY by this same
        // mutation (cartAdd/cartChange now request it) — it always reflects
        // the cart AFTER the mutation, so removing the gift instantly clears
        // the cart-page gift row/selector with no stale-GET race. On non-cart
        // pages `data.sections` is absent and this is a no-op.
        applyCartSections(data);
        unhideGiftRoots(); // freshly-swapped section markup ships `hidden` again
        // Refresh the drawer + header count too (its own re-render, no extra
        // cart-page fetch) so BOTH surfaces reflect the change together.
        return refreshDrawerSurface();
      })
      .catch(function (err) {
        // Network failure: revert busy, leave the cart truthful (we never
        // half-apply a mutation — each action() is a single atomic call),
        // console.warn only — no user-facing toast/copy per spec.
        console.warn('[bavona-gift] cart mutation failed; cart left as last known-good state', err);
        if (typeof onFail === 'function') onFail(err);
      })
      .then(function () {
        busy = false;
        // One follow-up pass on the post-mutation cart, so a second needed
        // correction (butter, then pouch) lands without waiting for the next
        // cart event. Only after a SUCCESS: a failed call never re-triggers
        // itself. reconcile() is idempotent, so on a correct cart this is a
        // no-op and the chain ends.
        if (ok) {
          return getCartData().then(reconcile).catch(function () {});
        }
      });
  }

  /* -----------------------------------------------------------------------
   * reconcile(cart) — idempotent safety net. Called after EVERY cart
   * mutation anywhere on the storefront (see the theme.miniCart patch and
   * the `cart:updated` listener below), plus once on initial load.
   *
   * PROOF this cannot loop:
   *   1. Bails instantly whenever `busy` is true. `busy` is set
   *      SYNCHRONOUSLY inside mutate(), before any `await`/microtask
   *      yield — so if two reconcile() calls are both queued (e.g. our
   *      own generateCart-triggered pass and a `cart:updated`-triggered
   *      pass fire back to back), whichever the JS engine runs FIRST
   *      either decides nothing needs doing (no-op, no race possible) or
   *      calls mutate() and flips `busy` to true before yielding control
   *      — guaranteeing the second call sees the up-to-date flag and
   *      bails. JS's single-threaded, run-to-completion semantics make
   *      this deterministic, not a best-effort race.
   *   2. Once `busy` clears, the cart is already correct (mutate() only
   *      resolves after refreshAllSurfaces, which re-renders from the
   *      POST-mutation cart). Any further reconcile() call — even one
   *      that was queued before busy cleared and runs just after —
   *      re-evaluates real cart state and lands on the terminal "already
   *      correct, do nothing" branch. It never re-adds/re-removes just
   *      because it ran again.
   *   3. Each individual reconcile() invocation performs AT MOST ONE
   *      mutation (single `if/return` chain below), so there is no
   *      compounding within a single call either.
   * --------------------------------------------------------------------- */
  function reconcile(cart) {
    if (busy) return;
    var items = (cart && cart.items) || [];
    var giftLines = items.filter(isGiftItem);
    // Lines that earn the perfume-butter gift: everything except the gift
    // itself and the Ritual Pouch (owner, 2026-10-02: a pouch-only order
    // does not receive the complimentary butter).
    var qualifyingItems = items.filter(function (it) { return !isGiftItem(it) && !isPouchItem(it); });

    // Defensive cleanup: this module's own busy-guarded flow can never
    // produce more than one gift line, but if the cart ever arrives in a
    // bad state from outside this module, collapse down to one — "exactly
    // ONE gift line ever" is a hard invariant.
    if (giftLines.length > 1) {
      mutate(function () { return cartChange(giftLines[1].key, 0); });
      return;
    }
    var giftLine = giftLines[0] || null;

    if (qualifyingItems.length === 0) {
      if (giftLine && giftLine.quantity > 0) {
        // Gift-only / pouch-only (or empty) cart — silently drop the gift.
        // No copy, no loss-aversion messaging, just a quiet correction.
        mutate(function () { return cartChange(giftLine.key, 0); });
        return;
      }
    } else {
      if (!giftLine) {
        var wantId = resolveDefaultVariantId();
        if (wantId != null) {
          mutate(function () { return cartAdd(wantId); });
          return;
        }
      } else if (giftLine.quantity > 1) {
        mutate(function () { return cartChange(giftLine.key, 1); });
        return;
      }
    }

    // Butter state is correct. Now the Ritual Pouch.
    if (!POUCH) return;
    var autoPouch = items.filter(isAutoPouchItem);
    if (autoPouch.length > 1) {
      mutate(function () { return cartChange(autoPouch[1].key, 0); });
      return;
    }
    var qualifyingCents = 0;
    qualifyingItems.forEach(function (it) {
      qualifyingCents += Number(it.original_line_price != null ? it.original_line_price : it.line_price) || 0;
    });
    var wantPouch = qualifyingCents >= Number(POUCH.thresholdCents || 0) && Number(POUCH.thresholdCents) > 0;
    // After the offer end the pouch is no longer free: never add it, and take back an auto-added one.
    if (Number(POUCH.endsEpoch) > 0 && Date.now() >= Number(POUCH.endsEpoch) * 1000) wantPouch = false;
    if (!wantPouch) {
      if (autoPouch.length === 1) {
        mutate(function () { return cartChange(autoPouch[0].key, 0); });
      }
      return;
    }
    // Threshold met: make sure ONE pouch is in the cart. A pouch the shopper
    // already added counts — the discount makes one of them free — so we
    // only auto-add when there is no pouch line at all.
    if (!items.some(isPouchItem) && POUCH.available && POUCH.variantId != null && !pouchAddFailed) {
      mutate(
        function () { return cartAdd(POUCH.variantId, { _pouch_gift: 'true' }); },
        function () { pouchAddFailed = true; }
      );
      return;
    }

    // Terminal, stable state: butter correct, pouch correct.
    // Deliberately do nothing — this is what stops the loop.
  }

  /* =======================================================================
   * 5. selectScent(newVariantId, rootEl) — the scent-swap flow. Exactly
   *    ONE add then ONE change, sequentially, guaranteeing the cart never
   *    holds two gift lines mid-flight. `rootEl` (the `.bv-gift` /
   *    `.bv-gift--drawer` node the click came from) is used only for the
   *    optimistic current-name label, reverted on failure.
   * ===================================================================== */
  function selectScent(newVariantId, rootEl) {
    if (busy) return;
    var target = giftVariantIds[String(newVariantId)];
    if (!target || !target.available) return; // unknown/sold-out — never select

    return getCartData().then(function (cart) {
      if (busy) return; // another mutation started while we awaited /cart.js
      var giftLine = getGiftLine(cart);
      if (giftLine && String(giftLine.variant_id) === String(newVariantId)) return; // already selected, no-op

      var nameEl = rootEl && rootEl.querySelector('[data-gift-current-name]');
      // Keep the <bdi> wrapper during the optimistic update so a Latin scent
      // name never loses bidi isolation in Arabic/RTL for the brief window
      // before refresh() restores the server markup.
      var previousHTML = nameEl ? nameEl.innerHTML : null;
      if (nameEl) nameEl.innerHTML = '<bdi>' + escapeHtml(target.title) + '</bdi>';

      busy = true;
      var cartSectionIds = Object.keys(window.__bvCartRefresh || {});
      return cartAdd(newVariantId)
        .then(function () {
          if (!giftLine) return null;
          // Remove the old gift line AND get the freshly-rendered cart
          // section(s) back in the SAME POST response (atomic with the
          // mutation, never cached) — then apply them so the cart-page gift
          // row image + selector update instantly. A separate GET ?sections=
          // could be CDN-cached/stale (the original "waits for reload" bug).
          return fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({
              id: giftLine.key,
              quantity: 0,
              sections: cartSectionIds.join(','),
              sections_url: window.location.pathname
            })
          }).then(function (r) {
            if (!r.ok) return r.json().then(function (d) { throw d; });
            return r.json();
          }).then(function (data) {
            var applyMap = window.__bvCartApply || {};
            if (data && data.sections) {
              cartSectionIds.forEach(function (id) {
                if (data.sections[id] && typeof applyMap[id] === 'function') {
                  applyMap[id](data.sections[id]);
                }
              });
            }
          });
        })
        .then(function () {
          // Cart page already updated atomically above; refresh the drawer
          // surface too. If there was NO prior gift line to remove, fall back
          // to a full surface refresh so the newly-added gift renders.
          return giftLine ? refreshDrawerSurface() : refreshAllSurfaces();
        })
        .then(function () { unhideGiftRoots(); })
        .catch(function (err) {
          console.warn('[bavona-gift] scent swap failed; cart left as last known-good state', err);
          if (nameEl && previousHTML != null) nameEl.innerHTML = previousHTML;
        })
        .then(function () { busy = false; });
    });
  }

  /* =======================================================================
   * 6. Surface refresh — re-renders the drawer (via theme.miniCart, once
   *    patched below) and the cart page's own section (via the exposed
   *    window.__bvCartRefresh hook) WITHOUT mutating the cart again.
   * ===================================================================== */
  function refreshAllSurfaces() {
    var tasks = [];
    if (window.theme && window.theme.miniCart) {
      if (typeof window.theme.miniCart.generateCart === 'function') {
        tasks.push(Promise.resolve(window.theme.miniCart.generateCart()));
      }
      if (typeof window.theme.miniCart.updateElements === 'function') {
        tasks.push(Promise.resolve(window.theme.miniCart.updateElements()));
      }
    }
    var refreshers = window.__bvCartRefresh || {};
    Object.keys(refreshers).forEach(function (id) {
      if (typeof refreshers[id] === 'function') tasks.push(refreshers[id]());
    });
    // The cart-page Sections-API refresh (window.__bvCartRefresh) swaps in
    // fresh section markup that always ships `hidden` on `.bv-gift` by
    // Liquid design (see bavona-gift-selector.liquid) and does NOT dispatch
    // `cart:updated` — so nothing else re-unhides it after a scent swap.
    // Always re-run unhideGiftRoots() once every refresh task settles,
    // success or failure, so the selector never vanishes post-swap.
    return Promise.all(tasks).then(function () { unhideGiftRoots(); }).catch(function () { unhideGiftRoots(); });
  }

  // Drawer-only refresh (the cart page is updated atomically by selectScent's
  // sections-in-POST apply, so it must NOT also GET-refresh — that would
  // re-fetch and could flash stale content).
  function refreshDrawerSurface() {
    var tasks = [];
    if (window.theme && window.theme.miniCart) {
      if (typeof window.theme.miniCart.generateCart === 'function') {
        tasks.push(Promise.resolve(window.theme.miniCart.generateCart()));
      }
      if (typeof window.theme.miniCart.updateElements === 'function') {
        tasks.push(Promise.resolve(window.theme.miniCart.updateElements()));
      }
    }
    return Promise.all(tasks).catch(function () {});
  }

  /* =======================================================================
   * 7. Cart-page selector: unhide the server-rendered root once we know
   *    the feature is enabled. Re-run after every Sections-API refresh —
   *    the fresh markup always ships `hidden` again by Liquid design.
   * ===================================================================== */
  function unhideGiftRoots() {
    document.querySelectorAll('.bv-gift[data-bavona-gift][hidden]').forEach(function (el) {
      el.removeAttribute('hidden');
    });
    // Runs on load and after every Sections-API swap, so the cart page's
    // freshly server-rendered options get the `:has()`-fallback class too.
    syncChipState(document);
  }

  /* =======================================================================
   * 8. Drawer variant — reconstructed entirely from island data. Only
   *    rendered when a gift line is actually in the cart (mirrors the
   *    cart-page's own gating: no gift line yet ⇒ nothing to show).
   * ===================================================================== */
  // One scent chip in the drawer's always-visible strip. Same radio contract
  // as the cart page (`name="bavona-gift-scent"` + `data-variant-id`), so the
  // single delegated `change` listener in section 9 drives both surfaces.
  function buildChipHTML(v, checked, strings) {
    var soldout = !v.available;
    var img = v.image
      ? '<img class="bv-gift__chip-img" loading="lazy" width="56" height="56" src="' + escapeHtml(v.image) + '" alt="">'
      : '<span class="bv-gift__chip-img bv-gift__chip-img--empty" aria-hidden="true"></span>';
    // Sold-out state is carried in the accessible name too — a dimmed chip
    // alone communicates nothing to a screen reader.
    var ariaLabel = soldout ? ' aria-label="' + escapeHtml(v.title + ' — ' + strings.soldOut) + '"' : '';
    return (
      '<label class="bv-gift__chip' + (soldout ? ' bv-gift__chip--soldout' : '') + '">' +
        '<input type="radio" name="bavona-gift-scent" value="' + v.id + '" data-variant-id="' + v.id +
          '" data-family="' + escapeHtml(v.family || '') + '"' + ariaLabel +
          (checked ? ' checked' : '') + (soldout ? ' disabled' : '') + '>' +
        img +
        '<span class="bv-gift__chip-name"><bdi>' + escapeHtml(v.title) + '</bdi></span>' +
        (soldout ? '<span class="bv-gift__chip-soldout">' + escapeHtml(strings.soldOut) + '</span>' : '') +
      '</label>'
    );
  }

  /**
   * Drawer variant — the scents are ALWAYS VISIBLE here, never behind a
   * disclosure. The previous build hid them behind a 10px uppercase
   * <summary> styled exactly like the brand's decorative eyebrow labels;
   * customers read it as a caption and never tapped it, so the gift shipped
   * in the default scent. On a narrow drawer a horizontal strip of the 12
   * scents costs less vertical space than the cart page's grid and removes
   * the discovery step entirely.
   *
   * The cart PAGE keeps its <details> panel — it has room for a full heading,
   * subheading and family-grouped grid, so discovery was never the problem
   * there. Family legends are dropped in the drawer (a flat strip reads
   * better at this width) but the cart-page grouping order is preserved.
   */
  function buildDrawerBlock(cart) {
    var giftLine = getGiftLine(cart);
    if (!giftLine) return ''; // nothing in the cart to represent yet

    var strings = getDrawerStrings();
    var currentId = giftLine.variant_id;

    // Preserve the cart page's cluster ORDER (warm → floral → everything
    // else) without rendering the legends.
    var KNOWN_FAMILIES = { 'warm-gourmand': true, 'floral-fresh': true };
    var ordered = []
      .concat(variants.filter(function (v) { return v.family === 'warm-gourmand'; }))
      .concat(variants.filter(function (v) { return v.family === 'floral-fresh'; }))
      .concat(variants.filter(function (v) { return !KNOWN_FAMILIES[v.family]; }));

    var chips = ordered.map(function (v) {
      return buildChipHTML(v, String(v.id) === String(currentId), strings);
    }).join('');

    return (
      '<div class="bv-gift bv-gift--drawer" data-bavona-gift-drawer data-gift-selected-template="' + escapeHtml(strings.selectedPrefix) + '">' +
        '<p class="bv-gift__lede">' + escapeHtml(strings.drawerLede) + '</p>' +
        '<div class="bv-gift__strip" data-gift-strip role="radiogroup" aria-label="' + escapeHtml(strings.ariaChoose) + '">' +
          chips +
        '</div>' +
      '</div>'
    );
  }

  // Bring the selected chip into view so a scent late in the strip still
  // reads as "chosen" rather than leaving the strip looking untouched.
  // `behavior:'auto'` (never 'smooth') — this fires on every drawer render,
  // and an animated scroll on open would be motion nobody asked for.
  function revealSelectedChip(container) {
    var strip = container && container.querySelector('[data-gift-strip]');
    if (!strip) return;
    var checked = strip.querySelector('input[name="bavona-gift-scent"]:checked');
    var chip = checked && checked.closest('.bv-gift__chip');
    if (!chip || typeof chip.scrollIntoView !== 'function') return;
    try {
      chip.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'auto' });
    } catch (e) {
      // Older Safari: options-object form unsupported — the default alignment
      // is acceptable, and scrolling the drawer itself is prevented by the
      // `block:'nearest'` intent, so simply skip rather than scroll the page.
    }
  }

  var DRAWER_STYLE_ID = 'bavona-gift-drawer-style';
  function ensureDrawerStyle() {
    if (document.getElementById(DRAWER_STYLE_ID)) return;
    var css =
      '.bv-gift--drawer{max-width:100%;margin:16px 0 0;padding:16px 0 4px;border-top:1px solid rgba(43,57,44,0.14);font-family:"Lora",serif;color:#2b392c;text-align:start;box-sizing:border-box;}' +
      '.bv-gift--drawer *{box-sizing:border-box;}' +
      // The invitation. 13px at full opacity — deliberately NOT the 10px
      // uppercase eyebrow treatment, which reads as a decorative caption in
      // this brand's visual language and is why the old control was missed.
      '.bv-gift--drawer .bv-gift__lede{font-size:13px;line-height:1.5;margin:0 0 12px;color:#2b392c;}' +
      // Horizontal strip: at ~76px per chip roughly four and a half are
      // visible on a 375px drawer, so the clipped chip is itself the
      // affordance that more scents exist.
      '.bv-gift--drawer .bv-gift__strip{display:flex;gap:8px;overflow-x:auto;overflow-y:hidden;-webkit-overflow-scrolling:touch;scroll-snap-type:x proximity;padding:2px 2px 10px;margin:0 -2px;scrollbar-width:thin;scrollbar-color:rgba(43,57,44,0.3) transparent;overscroll-behavior-x:contain;}' +
      '.bv-gift--drawer .bv-gift__strip::-webkit-scrollbar{height:3px;}' +
      '.bv-gift--drawer .bv-gift__strip::-webkit-scrollbar-thumb{background:rgba(43,57,44,0.3);}' +
      '.bv-gift--drawer .bv-gift__strip::-webkit-scrollbar-track{background:transparent;}' +
      // Crisp corners per the design system — never rounded swatches.
      '.bv-gift--drawer .bv-gift__chip{position:relative;flex:0 0 auto;width:76px;min-height:44px;scroll-snap-align:start;display:flex;flex-direction:column;align-items:center;text-align:center;gap:6px;cursor:pointer;touch-action:manipulation;padding:8px 4px 8px;border:1px solid rgba(43,57,44,0.2);background:#fff;transition:border-color 200ms ease,background-color 200ms ease;}' +
      '.bv-gift--drawer .bv-gift__chip:has(input:checked){border-color:#937b25;background:#faf8f3;}' +
      '.bv-gift--drawer .bv-gift__chip:not(.bv-gift__chip--soldout):hover{border-color:#ce7939;}' +
      '.bv-gift--drawer .bv-gift__chip:has(input:focus-visible){outline:2px solid #2b392c;outline-offset:2px;}' +
      '.bv-gift--drawer .bv-gift__chip input[type="radio"]{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;}' +
      '.bv-gift--drawer .bv-gift__chip-img{width:56px;height:56px;object-fit:cover;flex:none;background:#f4f0e8;display:block;}' +
      '.bv-gift--drawer .bv-gift__chip-img--empty{background:#937b25;}' +
      '.bv-gift--drawer .bv-gift__chip-name{font-size:11px;line-height:1.3;}' +
      '.bv-gift--drawer .bv-gift__chip--soldout{opacity:0.45;cursor:not-allowed;}' +
      '.bv-gift--drawer .bv-gift__chip--soldout input{cursor:not-allowed;}' +
      '.bv-gift--drawer .bv-gift__chip-soldout{font-size:9px;letter-spacing:0.06em;text-transform:uppercase;color:#282828;opacity:0.8;}' +
      // :has() is unsupported on iOS < 15.4 — without a fallback the selected
      // chip would be indistinguishable there. JS mirrors the state onto the
      // label so selection stays visible. See syncChipState().
      '.bv-gift--drawer .bv-gift__chip.is-selected{border-color:#937b25;background:#faf8f3;}' +
      '@media (prefers-reduced-motion:reduce){.bv-gift--drawer .bv-gift__chip{transition:none;}.bv-gift--drawer .bv-gift__strip{scroll-behavior:auto;}}' +
      '[dir="rtl"] .bv-gift--drawer{font-family:"Amiri","Lora",serif;}';
    var styleEl = document.createElement('style');
    styleEl.id = DRAWER_STYLE_ID;
    styleEl.textContent = css;
    document.head.appendChild(styleEl);
  }

  /**
   * Mirror each radio's :checked state onto its label as `.is-selected`.
   *
   * The selected-state styling relies on `:has(input:checked)`, which iOS
   * Safari only supports from 15.4. Without this mirror, a shopper on an
   * older iPhone — a meaningful slice of an Egyptian mobile audience — sees
   * twelve identical chips and no indication of which scent is theirs.
   * Covers the drawer chips AND the cart page's own options, which carry the
   * same latent gap.
   */
  function syncChipState(scope) {
    var root = scope || document;
    var labels = root.querySelectorAll('.bv-gift__chip, .bv-gift__opt');
    Array.prototype.forEach.call(labels, function (label) {
      var input = label.querySelector('input[name="bavona-gift-scent"]');
      label.classList.toggle('is-selected', !!(input && input.checked));
    });
  }

  function injectDrawerGift(cart) {
    var container = document.querySelector('.js-mini-cart-content');
    if (!container) return;
    // generateCart() always wipes this container via .html(), so there is
    // normally nothing stale here — this guard just protects against any
    // future change to that assumption (idempotent either way).
    var stale = container.querySelector('[data-bavona-gift-drawer]');
    if (stale) stale.remove();

    var html = buildDrawerBlock(cart);
    if (!html) return; // no gift line yet — nothing to inject

    ensureDrawerStyle();
    container.insertAdjacentHTML('beforeend', html);
    syncChipState(container);
    revealSelectedChip(container);
  }

  // Decorate the gift's own mini-cart-item row (built generically by
  // theme.miniCart.generateCart for every line): replace its price with
  // the localized "Complimentary" word and drop its remove (×) control —
  // qty is locked to 1 and fully auto-managed by reconcile(), never
  // manually removable.
  function markGiftRow(cart) {
    var giftLine = getGiftLine(cart);
    var container = document.querySelector('.js-mini-cart-content');
    if (!giftLine || !container) return;

    var removeBtn = container.querySelector('.js-remove-mini-cart[data-id="' + giftLine.variant_id + '"]');
    if (!removeBtn) return;
    var row = removeBtn.closest('.mini-cart-item');
    if (!row) return;

    var strings = getDrawerStrings();
    var currentVariant = giftVariantIds[String(giftLine.variant_id)];
    var scentName = currentVariant ? currentVariant.title : (giftLine.variant_title || '');

    var titleLink = row.querySelector('.mini-cart-meta a');
    if (titleLink) {
      // Isolate the (Latin) scent name inside the localized template so it
      // never reorders when embedded in Arabic RTL text — mirrors the
      // <bdi> treatment already used in bavona-cart.liquid:261.
      var tmplParts = String(strings.selectedPrefix || '').split('{scent}');
      titleLink.innerHTML = escapeHtml(tmplParts[0]) + '<bdi>' + escapeHtml(scentName) + '</bdi>' + escapeHtml(tmplParts[1] || '');
      // The gift is never a shoppable product — neutralize its link to the
      // hidden PDP (name + thumbnail) so it isn't discoverable from the drawer.
      titleLink.removeAttribute('href');
      titleLink.style.cursor = 'default';
      titleLink.style.pointerEvents = 'none';
    }
    var imageLink = row.querySelector('a.mini-cart-image');
    if (imageLink) {
      imageLink.removeAttribute('href');
      imageLink.style.cursor = 'default';
      imageLink.style.pointerEvents = 'none';
    }

    var priceEl = row.querySelector('.mini-cart-meta span');
    // Say "Complimentary" ONLY when the line actually costs nothing. This used
    // to also fire whenever any qualifying item was in the cart, on the
    // assumption that a present real item meant the Gift-with-Purchase BXGY had
    // applied. It did not: the BXGY could not combine with the BAVONA-SET-*
    // tier codes, so applying a tier silently dropped it, the bag still read
    // "Complimentary", and the customer was charged EGP 150 at checkout.
    // Never assert a price the cart has not actually been given — if the
    // discount is missing, showing the real price is the honest failure.
    if (priceEl && giftLine.final_line_price === 0) {
      priceEl.textContent = ' ' + strings.complimentary;
    }

    var qtyEl = row.querySelector('.mini-cart-meta b');
    if (qtyEl) qtyEl.remove(); // qty is always 1 — the "x 1" suffix is noise here

    removeBtn.remove();
    row.classList.add('bv-gift-row');
  }

  // guardDrawerCheckout(cart): the complimentary gift is never purchasable on
  // its own, so while the drawer is momentarily showing a gift-only cart
  // (between removing the last real item and reconcile() clearing the orphan
  // gift), disable the drawer's Checkout button so it can't be tapped against
  // a gift-only cart. Reuses the theme's own `.disabled` class (see
  // theme.js updateElements, which disables the same button on an empty cart).
  // Once reconcile empties the cart, updateElements takes over and keeps it
  // disabled; when a real item is present we clear the guard and let the theme
  // manage it normally.
  function guardDrawerCheckout(cart) {
    var btns = document.querySelectorAll('.js-cart-btn-checkout');
    if (!btns.length) return;
    var items = (cart && cart.items) || [];
    // Checkout is valid ONLY when a qualifying (non-gift) item is present. An
    // empty OR gift-only cart must not be checkoutable — the gift is never
    // purchasable on its own. The checkout control is now a real
    // <button type="submit">, so set the actual `disabled` property (the
    // `.disabled` class is styling-only — no pointer-events:none — so a class
    // alone would not truly block the click).
    var hasRealItem = items.some(function (it) { return !isGiftItem(it); });
    btns.forEach(function (btn) {
      if (hasRealItem) {
        btn.classList.remove('disabled');
        btn.removeAttribute('aria-disabled');
        if (btn.tagName === 'BUTTON') btn.disabled = false;
      } else {
        btn.classList.add('disabled');
        btn.setAttribute('aria-disabled', 'true');
        if (btn.tagName === 'BUTTON') btn.disabled = true;
      }
    });
  }

  /* =======================================================================
   * 9. Delegated UI wiring — one set of listeners covers BOTH surfaces
   *    (server-rendered cart-page selector + JS-built drawer variant)
   *    since both share the exact same class/data-attribute contract.
   *    Delegated on `document` so Sections-API DOM swaps (cart page) and
   *    drawer re-renders (generateCart) never require re-binding.
   * ===================================================================== */
  function syncPanelAria(panel) {
    var root = panel.closest('[data-bavona-gift], [data-bavona-gift-drawer]');
    var btn = root && root.querySelector('[data-gift-change]');
    if (btn) btn.setAttribute('aria-expanded', panel.open ? 'true' : 'false');
  }

  function wireDelegatedEvents() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('[data-gift-change]');
      if (!btn) return;
      // The change toggle is a native <summary>: let it open/close the
      // <details> itself (the capturing `toggle` listener below keeps aria in
      // sync). preventDefault + a manual toggle here would double-fire and
      // cancel out, so bail for summaries.
      if (btn.tagName === 'SUMMARY') return;
      e.preventDefault();
      var root = btn.closest('[data-bavona-gift], [data-bavona-gift-drawer]');
      var panel = root && root.querySelector('[data-gift-panel]');
      if (!panel) return;
      panel.open = !panel.open;
      syncPanelAria(panel); // belt-and-braces; the `toggle` listener below also syncs
    });

    // Native <details> `toggle` events do NOT bubble, but they DO reach
    // capturing-phase listeners on ancestors — this single delegated
    // listener keeps aria-expanded correct regardless of whether the
    // panel opened via our button, native <summary> click, or keyboard.
    document.addEventListener('toggle', function (e) {
      if (e.target && e.target.matches && e.target.matches('[data-gift-panel]')) {
        syncPanelAria(e.target);
      }
    }, true);

    document.addEventListener('change', function (e) {
      var input = e.target;
      if (!input.matches || !input.matches('input[name="bavona-gift-scent"]')) return;
      var root = input.closest('[data-bavona-gift], [data-bavona-gift-drawer]');
      if (!root) return;

      var variantId = input.getAttribute('data-variant-id') || input.value;
      var target = giftVariantIds[String(variantId)];
      // Sold-out radios are `disabled` in the markup and shouldn't fire a
      // change event at all — guard anyway, never auto/force-select one.
      if (!target || !target.available) return;

      // Repaint the selected state before the AJAX round trip, so the tap
      // feels immediate on the `:has()`-less browsers handled by syncChipState.
      syncChipState(root);

      // The drawer strip has no disclosure panel to close (the scents are
      // always visible there); the cart page still does.
      var panel = root.querySelector('[data-gift-panel]');
      if (panel) { panel.open = false; syncPanelAria(panel); }

      // Measurement: we had no idea how often the scent was actually changed,
      // which is exactly why the old hidden control went unnoticed for so
      // long. GTM is already installed (layout/theme.liquid) — this makes the
      // behaviour countable without adding a tag or a network call.
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'bavona_gift_scent_change',
          gift_scent: target.title,
          gift_variant_id: String(variantId),
          gift_surface: root.hasAttribute('data-bavona-gift-drawer') ? 'drawer' : 'cart_page'
        });
      } catch (e) { /* analytics must never block the selection itself */ }

      selectScent(variantId, root);
    });
  }

  /* =======================================================================
   * 10. theme.miniCart patch — mirrors the PROVEN precedent already in
   *     this codebase (the Arabic currency rewriter, ~theme.js line 5465):
   *     wrap generateCart/updateElements so that AFTER the original build
   *     runs, we inject the drawer gift block and reconcile. We do our own
   *     independent /cart.js fetch rather than trust the original's
   *     internal Shopify.getCart timing (network latency dominates the
   *     original's synchronous DOM write, so by the time OUR fetch
   *     resolves the original's `.html()` call has effectively always
   *     already landed).
   * ===================================================================== */
  // lastCart: most recent /cart.js snapshot fetched by afterMiniCartRender,
  // reused by the self-healing MutationObserver below (ensureMiniCartObserver)
  // so it never needs its own network round trip.
  var lastCart = null;

  var afterRenderPromise = null;
  function afterMiniCartRender() {
    if (afterRenderPromise) return afterRenderPromise; // coalesce back-to-back calls (updateElements + generateCart fire together on every add path)
    afterRenderPromise = new Promise(function (resolve) {
      setTimeout(function () {
        getCartData()
          .then(function (cart) {
            lastCart = cart;
            injectDrawerGift(cart);
            markGiftRow(cart);
            guardDrawerCheckout(cart); // block checkout on a transient gift-only drawer
            reconcile(cart); // busy-guarded — see loop-safety proof above
            ensureMiniCartObserver(); // lazily attach the self-heal observer once the container exists
            // Let the Arabic currency rewriter (and any other cart:updated
            // listener) re-scan the freshly injected drawer content.
            document.dispatchEvent(new CustomEvent('cart:updated'));
          })
          .catch(function (err) { console.warn('[bavona-gift] drawer refresh failed', err); })
          .then(function () {
            afterRenderPromise = null;
            resolve();
          });
      }, 0);
    });
    return afterRenderPromise;
  }

  /* -----------------------------------------------------------------------
   * ensureMiniCartObserver() — self-heal for the decoration race: theme.js's
   * own generateCart() does an async `.html()` write from ITS OWN internal
   * Shopify.getCart call, timed independently of our afterMiniCartRender()
   * fetch above. If the theme's write lands AFTER ours (observed
   * intermittently, drawer-only), it wipes our injected
   * `[data-bavona-gift-drawer]` block and restores the plain, removable,
   * undecorated gift row. This persistent observer watches the mini-cart
   * container for exactly that and repaints using the last-known cart data
   * — no extra network fetch needed.
   *
   * LOOP-GUARD (so observing our own repaint can never re-trigger itself):
   *   - Bails instantly while `busy` (a cart mutation is in flight; wait for
   *     it to settle and call afterMiniCartRender itself).
   *   - Bails when there's no `lastCart` yet, or no gift line in it — this
   *     observer only ever ADDS the gift decoration, never removes/mutates
   *     cart state.
   *   - Bails whenever BOTH the injected block AND the decorated row
   *     (`.bv-gift-row`) are already present — this is the terminal
   *     "already correct" branch. injectDrawerGift/markGiftRow only ever
   *     add what's missing, so once both are present a repaint is a no-op
   *     that would just re-trigger the observer pointlessly; skipping it
   *     here is what makes observer -> mutation -> observer structurally
   *     impossible, mirroring the reconcile()/busy proof above.
   * --------------------------------------------------------------------- */
  var miniCartObserverAttached = false;
  function ensureMiniCartObserver() {
    if (miniCartObserverAttached) return;
    var container = document.querySelector('.js-mini-cart-content');
    if (!container) return; // try again next time a render/observer-check runs
    miniCartObserverAttached = true;

    var observer = new MutationObserver(function () {
      if (busy) return; // a cart mutation is in flight — never race it
      if (!lastCart) return; // nothing to repaint from yet
      var giftLine = getGiftLine(lastCart);
      if (!giftLine) return; // no gift line to represent

      var hasBlock = !!container.querySelector('[data-bavona-gift-drawer]');
      var hasDecoratedRow = !!container.querySelector('.bv-gift-row');
      if (hasBlock && hasDecoratedRow) return; // already correct — the loop-guard

      injectDrawerGift(lastCart);
      markGiftRow(lastCart);
    });
    observer.observe(container, { childList: true, subtree: true });
  }

  function patchMiniCart(miniCart) {
    if (miniCart._bavonaGiftPatched) return;
    var origGenerateCart = miniCart.generateCart;
    var origUpdateElements = miniCart.updateElements;

    miniCart.generateCart = function () {
      var ret = origGenerateCart.apply(this, arguments);
      var p = afterMiniCartRender();
      return p || ret;
    };
    miniCart.updateElements = function () {
      var ret = origUpdateElements.apply(this, arguments);
      var p = afterMiniCartRender();
      return p || ret;
    };
    miniCart._bavonaGiftPatched = true;
  }

  function whenMiniCartReady(cb) {
    if (window.theme && window.theme.miniCart && typeof window.theme.miniCart.generateCart === 'function') {
      cb(window.theme.miniCart);
      return;
    }
    var tries = 0;
    var iv = setInterval(function () {
      tries++;
      if (window.theme && window.theme.miniCart && typeof window.theme.miniCart.generateCart === 'function') {
        clearInterval(iv);
        cb(window.theme.miniCart);
      } else if (tries > 40) { // ~4s at 100ms
        clearInterval(iv);
        console.warn('[bavona-gift] theme.miniCart never became available — drawer enhancement skipped, cart-page selector unaffected');
      }
    }, 100);
  }

  /* =======================================================================
   * 11. Init.
   * ===================================================================== */
  function init() {
    // Cart-page selector: independent of theme.miniCart.
    unhideGiftRoots();
    wireDelegatedEvents();

    // Cart-page mutations (qty stepper / remove) go through
    // sections/bavona-cart.liquid's own JS, which dispatches `cart:updated`
    // after every Sections-API swap but never touches theme.miniCart.
    document.addEventListener('cart:updated', function () {
      getCartData()
        .then(function (cart) {
          unhideGiftRoots(); // freshly-swapped section markup ships `hidden` again
          reconcile(cart);
        })
        .catch(function (err) { console.warn('[bavona-gift] cart:updated handling failed', err); });
    });

    // One reconciliation pass on load — covers carts that reached a
    // gift-missing/gift-only state before this feature existed or via
    // checkout back-navigation.
    getCartData()
      .then(reconcile)
      .catch(function (err) { console.warn('[bavona-gift] initial reconcile failed', err); });

    // Drawer enhancement — optional, best-effort, never blocks the
    // cart-page experience if theme.miniCart is unavailable for any reason.
    whenMiniCartReady(function (miniCart) {
      patchMiniCart(miniCart);
      // Paint the gift block onto whatever the drawer already rendered
      // before this deferred script finished loading (theme.js calls
      // generateCart() once, synchronously, before our patch exists).
      afterMiniCartRender();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
